import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import Table from "../../core/pagination/datatable";
import { all_routes } from "../../Router/all_routes";
import RefreshIcon from "../../core/common/tooltip-content/refresh";
import CollapesIcon from "../../core/common/tooltip-content/collapes";
import { DatePicker, message } from "antd";
import CommonFooter from "../../core/common/footer/commonFooter";
import {
  getInventoryReportFilters,
  getInventoryReportList,
  exportInventoryReportExcel,
  exportInventoryReportPdf,
} from "../../api/InventoryReportApi";

const DEFAULT_PRODUCT_IMAGE = `${process.env.PUBLIC_URL || ""}/assets/img/by-default.png`;
const IMAGE_BASE = (
  process.env.REACT_APP_API_IMAGE_BASE_URL ||
  "https://hamilton-shop-images.s3.ap-south-1.amazonaws.com"
).replace(/\/+$/, "");

const resolveImage = (raw) => {
  if (!raw) return DEFAULT_PRODUCT_IMAGE;
  const value = Array.isArray(raw) ? raw[0] : raw;
  if (typeof value !== "string") return DEFAULT_PRODUCT_IMAGE;
  if (/^data:image/i.test(value) || /^https?:\/\//i.test(value)) return value;
  return `${IMAGE_BASE}/${value.replace(/^\/+/, "")}`;
};

const InventoryReport = () => {
  const route = all_routes;
  const t = (key) => (window.translate ? window.translate(key) : key);
  const isArabic = (localStorage.getItem("lang") || "en").toLowerCase().startsWith("ar");

  const reactSelectStyles = useMemo(
    () => ({
      control: (base) => ({
        ...base,
        minHeight: 44,
        borderRadius: 10,
        borderColor: "#dfe3e8",
        boxShadow: "none",
      }),
      valueContainer: (base) => ({
        ...base,
        padding: "2px 10px",
      }),
      indicatorsContainer: (base) => ({
        ...base,
        height: 44,
      }),
      placeholder: (base) => ({
        ...base,
        width: "100%",
        textAlign: isArabic ? "right" : "left",
      }),
      singleValue: (base) => ({
        ...base,
        width: "100%",
        textAlign: isArabic ? "right" : "left",
      }),
      option: (base) => ({
        ...base,
        textAlign: isArabic ? "right" : "left",
      }),
      menu: (base) => ({
        ...base,
        zIndex: 20,
        direction: isArabic ? "rtl" : "ltr",
      }),
    }),
    [isArabic],
  );

  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const [tableData, setTableData] = useState([]);
  const [summary, setSummary] = useState({});
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 50,
    totalPages: 1,
  });

  const [filterOptions, setFilterOptions] = useState({
    categories: [],
    products: [],
    units: [],
    brands: [],
    warehouses: [],
    stores: [],
  });

  const [filters, setFilters] = useState({
    date: null,
    category: null,
    product: null,
    unit: null,
  });

  const categoryOptions = useMemo(() => {
    return (filterOptions.categories || []).map((item) => ({
      value: item?._id,
      label:
        item?.name?.[localStorage.getItem("lang") || "en"] ||
        item?.name?.en ||
        item?.name?.ar ||
        t("N/A"),
    }));
  }, [filterOptions.categories]);

  const productOptions = useMemo(() => {
    return (filterOptions.products || []).map((item) => ({
      value: item?._id,
      label:
        item?.productName?.[localStorage.getItem("lang") || "en"] ||
        item?.productName?.en ||
        item?.productName?.ar ||
        item?.sku ||
        t("N/A"),
    }));
  }, [filterOptions.products]);

  const unitOptions = useMemo(() => {
    return (filterOptions.units || []).map((item) => ({
      value: item?._id,
      label: item?.shortName || item?.unitName?.en || item?.unitName?.ar || t("N/A"),
    }));
  }, [filterOptions.units]);

  const fetchFilters = async () => {
    try {
      setFilterLoading(true);
      const response = await getInventoryReportFilters();
      setFilterOptions(response?.data || {});
    } catch (error) {
      message.error(error?.message || t("Failed to load inventory report filters"));
    } finally {
      setFilterLoading(false);
    }
  };

  const fetchInventoryReport = async (customFilters = filters) => {
    try {
      setLoading(true);

      const params = {
        page: 1,
        limit: 100,
        date: customFilters?.date ? customFilters.date.format("YYYY-MM-DD") : "",
        category: customFilters?.category?.value || "",
        product: customFilters?.product?.value || "",
        unit: customFilters?.unit?.value || "",
      };

      const response = await getInventoryReportList(params);
      const payload =
        response?.data &&
        (Array.isArray(response?.data?.data) || response?.data?.summary || response?.data?.pagination)
          ? response.data
          : response || {};

      const responseRows = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.rows)
        ? payload.rows
        : Array.isArray(payload?.data?.rows)
        ? payload.data.rows
        : [];

      const formattedData =
        responseRows.length > 0
          ? responseRows.map((item, index) => ({
              id: item?._id || `${item?.productId || "row"}-${index}`,
              key: item?._id || `${item?.productId || "row"}-${index}`,
              img: resolveImage(item?.image || item?.productImage || item?.raw?.image),
              productName: item?.productName || t("N/A"),
              sku: item?.sku || t("N/A"),
              category: item?.category || t("N/A"),
              brand: item?.brand || t("N/A"),
              unit: item?.unit || t("N/A"),
              instockQty: Number(item?.quantity || item?.instockQty || 0),
              raw: item,
            }))
          : [];

      setTableData(formattedData);
      setSummary(payload?.summary || {});
      setPagination(
        payload?.pagination || {
          total: formattedData.length,
          page: 1,
          limit: 100,
          totalPages: 1,
        }
      );
    } catch (error) {
      console.error("Inventory report fetch error:", error);
      message.error(error?.message || t("Failed to fetch inventory report"));
      setTableData([]);
      setSummary({});
      setPagination({
        total: 0,
        page: 1,
        limit: 50,
        totalPages: 1,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFilters();
    fetchInventoryReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGenerateReport = async (e) => {
    e.preventDefault();
    await fetchInventoryReport(filters);
  };

  const handleRefresh = async (e) => {
    e.preventDefault();
    await fetchFilters();
    await fetchInventoryReport(filters);
  };

  const getExportParams = () => {
    return {
      date: filters?.date ? filters.date.format("YYYY-MM-DD") : "",
      category: filters?.category?.value || "",
      product: filters?.product?.value || "",
      unit: filters?.unit?.value || "",
    };
  };

  const handleExportExcel = async () => {
    try {
      setExportLoading(true);
      await exportInventoryReportExcel(getExportParams());
      message.success(t("Inventory report excel downloaded successfully"));
    } catch (error) {
      message.error(error?.message || t("Failed to export excel"));
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportPdf = async () => {
    try {
      setExportLoading(true);
      await exportInventoryReportPdf(getExportParams());
      message.success(t("Inventory report pdf downloaded successfully"));
    } catch (error) {
      message.error(error?.message || t("Failed to export pdf"));
    } finally {
      setExportLoading(false);
    }
  };

  const columns = [
    {
      title: t("Product Name"),
      dataIndex: "productName",
      render: (text, record) => (
        <span className="productimgname">
          <Link to="#" className="product-img stock-img">
            <img
              src={record?.img || DEFAULT_PRODUCT_IMAGE}
              alt={text || t("product")}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = DEFAULT_PRODUCT_IMAGE;
              }}
            />
          </Link>
          <Link to="#">{text || t("N/A")}</Link>
        </span>
      ),
      sorter: (a, b) => (a?.productName || "").localeCompare(b?.productName || ""),
    },
    {
      title: t("SKU"),
      dataIndex: "sku",
      render: (text) => text || t("N/A"),
      sorter: (a, b) => (a?.sku || "").localeCompare(b?.sku || ""),
    },
    {
      title: t("Category"),
      dataIndex: "category",
      render: (text) => text || t("N/A"),
      sorter: (a, b) => (a?.category || "").localeCompare(b?.category || ""),
    },
    {
      title: t("Brand"),
      dataIndex: "brand",
      render: (text) => text || t("N/A"),
      sorter: (a, b) => (a?.brand || "").localeCompare(b?.brand || ""),
    },
    {
      title: t("Unit"),
      dataIndex: "unit",
      render: (text) => text || t("N/A"),
      sorter: (a, b) => (a?.unit || "").localeCompare(b?.unit || ""),
    },
    {
      title: t("Instock Qty"),
      dataIndex: "instockQty",
      render: (text) => Number(text || 0),
      sorter: (a, b) => Number(a?.instockQty || 0) - Number(b?.instockQty || 0),
    },
  ];

  const statCards = [
    {
      title: t("Total Rows"),
      value: summary?.totalRows || tableData.length || 0,
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      icon: "ti ti-layout-grid",
    },
    {
      title: t("Total Stock Qty"),
      value: summary?.totalStockQty || 0,
      gradient: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
      icon: "ti ti-package",
    },
    {
      title: t("Low Stock"),
      value: summary?.lowStockCount || 0,
      gradient: "linear-gradient(135deg, #f7971e 0%, #ffd200 100%)",
      icon: "ti ti-alert-triangle",
    },
    {
      title: t("Out Of Stock"),
      value: summary?.outOfStockCount || 0,
      gradient: "linear-gradient(135deg, #ee0979 0%, #ff6a00 100%)",
      icon: "ti ti-xbox-x",
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content" dir={isArabic ? "rtl" : "ltr"}>
          <div className="table-tab mb-3">
            <ul className="nav nav-pills gap-2 flex-wrap">
              <li className="nav-item">
                <Link className="nav-link active" to={route.inventoryreport}>
                  {t("Inventory Report")}
                </Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to={route.stockhistory}>
                  {t("Stock History")}
                </Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to={route.soldstock}>
                  {t("Sold Stock")}
                </Link>
              </li>
            </ul>
          </div>

          <div className="page-header mb-3">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>{t("Inventory")}</h4>
                <h6>{t("View Reports of Inventory")}</h6>
              </div>
            </div>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              <button
                type="button"
                className="btn px-3"
                onClick={handleExportExcel}
                disabled={exportLoading}
                style={{
                  border: "1px solid #0d6efd",
                  color: "#0d6efd",
                  background: "#fff",
                  minWidth: "130px",
                  minHeight: "42px",
                }}
              >
                {exportLoading ? t("Please wait...") : t("Export Excel")}
              </button>

              <button
                type="button"
                className="btn px-3"
                onClick={handleExportPdf}
                disabled={exportLoading}
                style={{
                  border: "1px solid #198754",
                  color: "#198754",
                  background: "#fff",
                  minWidth: "130px",
                  minHeight: "42px",
                }}
              >
                {exportLoading ? t("Please wait...") : t("Export PDF")}
              </button>

              <ul className="table-top-head" style={{ marginBottom: 0 }}>
                <li>
                  <Link to="#" onClick={handleRefresh}>
                    <RefreshIcon />
                  </Link>
                </li>
                <CollapesIcon />
              </ul>
            </div>
          </div>

          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body pb-1 pt-3">
              <form onSubmit={handleGenerateReport}>
                <div className="row g-3 align-items-end">
                  <div className="col-lg-10">
                    <div className="row g-3">
                      <div className="col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label fw-medium">{t("Choose Date")}</label>
                          <div className="input-icon-start position-relative">
                            <DatePicker
                              className="form-control datetimepicker"
                              placeholder={t("dd/mm/yyyy")}
                              value={filters.date}
                              onChange={(date) =>
                                setFilters((prev) => ({
                                  ...prev,
                                  date,
                                }))
                              }
                              format="DD/MM/YYYY"
                              style={{ width: "100%", height: 44, borderRadius: 10 }}
                            />
                            <span
                              className="input-icon-left"
                              style={isArabic ? { left: "auto", right: 10 } : undefined}
                            >
                              <i className="ti ti-calendar" />
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label fw-medium">{t("Category")}</label>
                          <Select
                            classNamePrefix="react-select"
                            options={categoryOptions}
                            placeholder={t("Choose")}
                            value={filters.category}
                            onChange={(selected) =>
                              setFilters((prev) => ({
                                ...prev,
                                category: selected,
                              }))
                            }
                            isClearable
                            isLoading={filterLoading}
                            styles={reactSelectStyles}
                          />
                        </div>
                      </div>

                      <div className="col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label fw-medium">{t("Products")}</label>
                          <Select
                            classNamePrefix="react-select"
                            options={productOptions}
                            placeholder={t("Choose")}
                            value={filters.product}
                            onChange={(selected) =>
                              setFilters((prev) => ({
                                ...prev,
                                product: selected,
                              }))
                            }
                            isClearable
                            isLoading={filterLoading}
                            styles={reactSelectStyles}
                          />
                        </div>
                      </div>

                      <div className="col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label fw-medium">{t("Units")}</label>
                          <Select
                            classNamePrefix="react-select"
                            options={unitOptions}
                            placeholder={t("Choose")}
                            value={filters.unit}
                            onChange={(selected) =>
                              setFilters((prev) => ({
                                ...prev,
                                unit: selected,
                              }))
                            }
                            isClearable
                            isLoading={filterLoading}
                            styles={reactSelectStyles}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-2">
                    <div className="mb-3">
                      <button
                        className="btn w-100"
                        type="submit"
                        disabled={loading}
                        style={{
                          background: "linear-gradient(135deg, #f7971e 0%, #ffd200 100%)",
                          border: "none",
                          color: "#fff",
                          fontWeight: 600,
                          minHeight: "44px",
                          boxShadow: "0 8px 18px rgba(247,151,30,0.25)",
                        }}
                      >
                        {loading ? t("Please wait...") : t("Generate Report")}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>

          <div className="row g-3 mb-4">
            {statCards.map((card, index) => (
              <div className="col-xl-3 col-sm-6 col-12 d-flex" key={index}>
                <div
                  className="card w-100 border-0 h-100"
                  style={{
                    background: card.gradient,
                    borderRadius: "16px",
                    overflow: "hidden",
                    boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
                  }}
                >
                  <div className="card-body d-flex justify-content-between align-items-center">
                    <div>
                      <h6
                        style={{
                          color: "rgba(255,255,255,0.9)",
                          marginBottom: "10px",
                          fontSize: "14px",
                          fontWeight: 500,
                        }}
                      >
                        {card.title}
                      </h6>
                      <h2
                        style={{
                          color: "#fff",
                          margin: 0,
                          fontWeight: 700,
                        }}
                      >
                        {card.value}
                      </h2>
                    </div>
                    <div
                      style={{
                        width: "56px",
                        height: "56px",
                        borderRadius: "14px",
                        background: "rgba(255,255,255,0.18)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontSize: "24px",
                      }}
                    >
                      <i className={card.icon} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="card table-list-card border-0 shadow-sm">
            <div className="card-body">
              <div
                className={`d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2 ${
                  isArabic ? "text-end" : "text-start"
                }`}
              >
                <div>
                  <h5 className="mb-1">{t("Inventory Table")}</h5>
                  <p className="text-muted mb-0">
                    {t("Showing")}{" "}
                    <strong>{Array.isArray(tableData) ? tableData.length : 0}</strong>{" "}
                    {t("records")}
                  </p>
                </div>
                <div className="text-muted">
                  {t("Total")}: <strong>{pagination?.total || 0}</strong>
                </div>
              </div>

              <div className="table-responsive" style={{ minHeight: 280 }}>
                <Table
                  props={`inventory-${tableData.length}`}
                  columns={columns}
                  dataSource={Array.isArray(tableData) && tableData.length > 0 ? tableData : []}
                  loading={loading}
                />
              </div>

              <div
                className={`d-flex justify-content-between align-items-center mt-3 pt-3 flex-wrap gap-2 ${
                  isArabic ? "text-end" : "text-start"
                }`}
                style={{ borderTop: "1px solid #eef1f4" }}
              >
                <div>
                  <strong>{t("Total")}:</strong> {pagination?.total || 0}
                </div>
                <div>
                  <strong>{t("Page")}:</strong> {pagination?.page || 1} / {pagination?.totalPages || 1}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CommonFooter />
    </>
  );
};

export default InventoryReport;
